import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Send,
  FileText,
  Calendar,
  User,
  IndianRupee,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Trash2,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate";
import offerService from "../../../api/services/offerService";
import { API_BASE_URL } from "../../../api/config";
import {
  useCustomAlert,
  useCustomConfirm,
} from "../../../hooks/useCustomAlert";
import CustomAlert from "../../../components/common/CustomAlert";
import CustomConfirm from "../../../components/common/CustomConfirm";
import { formatDate } from "../../../utils/stringUtils";

const ViewOffer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const { confirmState, showConfirm, hideConfirm } = useCustomConfirm();
  const greenButtonClasses =
    "bg-brand text-white hover:bg-brand-dark text-white border-transparent";

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        setError(null);

        // Auth token check

        const result = await offerService.getOfferById(id);
        console.log("📦 Offer fetch result:", result);
        console.log("📦 Result type:", typeof result);
        console.log("📦 Result keys:", Object.keys(result || {}));
        console.log("📦 Response code:", result?.responseCode);
        console.log("📦 Has data:", !!result?.data);

        // Check if result has the expected structure
        if (result && result.responseCode === 200 && result.data) {
          // Standard API response format
          setOffer(result.data);

        } else if (result && result.id && result.offerId) {
          // Direct data format (no wrapper)
          setOffer(result);

        } else {
          const errorMsg = result?.responseMessage || "Failed to fetch offer";

          setError(errorMsg);
        }
      } catch (err) {

        setError("Failed to fetch offer details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOffer();
    }
  }, [id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "sent":
        return "bg-blue-100 text-blue-800";
      case "accepted":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "expired":
        return "bg-gray-100 text-gray-800";
      case "withdrawn":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "draft":
        return <FileText className="h-4 w-4" />;
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "sent":
        return <Send className="h-4 w-4" />;
      case "accepted":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      case "expired":
        return <AlertCircle className="h-4 w-4" />;
      case "withdrawn":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency === "INR" ? "INR" : currency === "USD" ? "USD" : "EUR",
      minimumFractionDigits: 0,
    });
    return formatter.format(amount);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      const result = await offerService.updateOffer(id, { status: newStatus });

      if (result && result.responseCode === 200) {
        setOffer((prev) => ({ ...prev, status: newStatus }));
        showSuccess(`Offer status updated to ${newStatus}`);
      } else {
        showError(result?.responseMessage || "Failed to update offer status");
      }
    } catch (err) {

      showError("Failed to update offer status");
    } finally {
      setUpdating(false);
    }
  };

  const handleSendOffer = async () => {
    try {
      setUpdating(true);
      const result = await offerService.sendOfferEmail(id);

      if (result && result.responseCode === 200) {
        setOffer((prev) => ({
          ...prev,
          status: "sent",
          sentDate: new Date().toISOString().split("T")[0],
        }));
        showSuccess("Offer email sent successfully to candidate!");
      } else {
        showError(result?.responseMessage || "Failed to send offer email");
      }
    } catch (err) {

      showError("Failed to send offer email");
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const fileUrl = doc?.fileUrl || "";
      const fallbackName = doc?.originalName || doc?.filename || "document";
      const filename =
        doc?.filename || (fileUrl ? fileUrl.split("/").pop() : "");
      if (!filename) {
        showError("Document not available");
        return;
      }

      const parseFilenameFromContentDisposition = (contentDisposition) => {
        if (!contentDisposition) return "";

        const utf8Match = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)/i,
        );
        if (utf8Match?.[1]) {
          try {
            return decodeURIComponent(utf8Match[1]).replace(/(^"|"$)/g, "");
          } catch (_) {
            return utf8Match[1].replace(/(^"|"$)/g, "");
          }
        }

        const simpleMatch = contentDisposition.match(/filename=([^;]+)/i);
        return simpleMatch?.[1]
          ? simpleMatch[1].trim().replace(/(^"|"$)/g, "")
          : "";
      };

      const ensureFilenameHasExtension = (name, mimeType) => {
        const clean = String(name || fallbackName || "document").trim();
        if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

        const lowerType = String(mimeType || "").toLowerCase();
        if (lowerType === "application/pdf") return `${clean}.pdf`;
        if (lowerType === "application/msword") return `${clean}.doc`;
        if (
          lowerType ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          return `${clean}.docx`;
        if (lowerType === "text/plain") return `${clean}.txt`;
        if (lowerType === "image/jpeg") return `${clean}.jpg`;
        if (lowerType === "image/png") return `${clean}.png`;
        if (lowerType === "image/gif") return `${clean}.gif`;

        return `${clean}.pdf`;
      };

      const encodedFilename = encodeURIComponent(filename);
      const resp = await fetch(
        `${API_BASE_URL}/recruitment/offers/download/${encodedFilename}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        },
      );
      if (!resp.ok)
        throw new Error(`Download failed: ${resp.status} ${resp.statusText}`);

      const blob = await resp.blob();
      const headerFileName = parseFilenameFromContentDisposition(
        resp.headers.get("content-disposition"),
      );
      const resolvedName = ensureFilenameHasExtension(
        headerFileName || fallbackName,
        blob.type,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resolvedName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      showSuccess("Document downloaded");
    } catch (error) {

      showError("Failed to download document: " + error.message);
    }
  };

  const handleDeleteOffer = () => {


    if (!id) {

      showError("No offer ID available for deletion");
      return;
    }

    if (!offer) {

      showError("Offer data not loaded");
      return;
    }

    showConfirm({
      title: "Delete Offer",
      message: `Are you sure you want to delete this offer for ${offer.candidateName || "the candidate"}? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
      onConfirm: async () => {
        try {
          const result = await offerService.deleteOffer(id);

          if (
            result &&
            (result.responseCode === 200 ||
              result.success === true ||
              result.responseMessage?.includes("successfully"))
          ) {

            showSuccess("Offer deleted successfully");
            navigate("/offer_management");
          } else {
            const errorMessage =
              result?.responseMessage ||
              result?.message ||
              "Failed to delete offer";

            showError(errorMessage);
          }
        } catch (err) {

          showError(
            "Failed to delete offer: " + (err.message || "Network error"),
          );
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading offer details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error loading offer
        </h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <div className="space-x-3">
          <Button
            variant="primary"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate("/offer_management")}
            className={greenButtonClasses}
          >
            Back to Offers
          </Button>
          <Button
            variant="secondary"
            onClick={() => window.location.reload()}
            className={greenButtonClasses}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Offer not found
        </h3>
        <p className="text-gray-500 mb-4">
          The offer you're looking for doesn't exist.
        </p>
        <Button
          variant="primary"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate("/offer_management")}
          className={greenButtonClasses}
        >
          Back to Offers
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="secondary"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
            disabled={updating}
            className={greenButtonClasses}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Offer Details</h1>
            <p className="text-gray-600">View and manage job offer details</p>
          </div>
        </div>
        <div className="flex space-x-3">
          {offer.status !== "sent" && offer.status !== "accepted" && (
            <PermissionGate permission="recruitment" action="update">
              <Button
                variant="secondary"
                icon={<Edit className="h-4 w-4" />}
                onClick={() => navigate(`/edit_offer/${id}`)}
                disabled={updating}
                className={greenButtonClasses}
              >
                Edit Offer
              </Button>
            </PermissionGate>
          )}
          {offer.status === "draft" && (
            <PermissionGate permission="recruitment" action="update">
              <Button
                variant="primary"
                icon={<Send className="h-4 w-4" />}
                onClick={handleSendOffer}
                disabled={updating}
                className={greenButtonClasses}
              >
                {updating ? "Sending..." : "Send Offer"}
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="recruitment" action="delete">
            <Button
              variant="secondary"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() => {

                handleDeleteOffer();
              }}
              disabled={updating}
              className={greenButtonClasses}
            >
              Delete
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Offer Status Banner */}
      <div
        className={`p-4 rounded-lg border-l-4 ${getStatusColor(offer.status).replace("bg-", "bg-").replace("text-", "text-")}`}
      >
        <div className="flex items-center">
          {getStatusIcon(offer.status)}
          <div className="ml-3">
            <h3 className="text-sm font-medium">
              Offer Status: <span className="capitalize">{offer.status}</span>
            </h3>
            <p className="text-sm opacity-75">
              {offer.status === "draft" &&
                "Offer is in draft mode and ready to be sent"}
              {offer.status === "pending" && "Waiting for candidate response"}
              {offer.status === "sent" && "Offer has been sent to candidate"}
              {offer.status === "accepted" &&
                "Candidate has accepted the offer"}
              {offer.status === "rejected" &&
                "Candidate has rejected the offer"}
              {offer.status === "expired" && "Offer has expired"}
              {offer.status === "withdrawn" && "Offer has been withdrawn"}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Candidate Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Candidate Information
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Name
                </label>
                <p className="text-lg font-semibold text-gray-900 capitalize break-words whitespace-pre-wrap">
                  {offer.candidateName || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email
                </label>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {offer.candidateEmail || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Phone
                </label>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {offer.candidatePhone || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Job Position
                </label>
                <p className="text-gray-900 capitalize break-words whitespace-pre-wrap">
                  {offer.jobTitle || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Department
                </label>
                <p className="text-gray-900 capitalize break-words whitespace-pre-wrap">
                  {offer.jobDepartment || offer.department || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Offer Type
                </label>
                <p className="text-gray-900 capitalize break-words whitespace-pre-wrap">
                  {offer.offerType || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Offer Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <IndianRupee className="h-5 w-5 mr-2" />
                Offer Details
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Salary
                </label>
                <p className="text-2xl font-bold text-gray-900">
                  {offer.salary
                    ? formatCurrency(offer.salary, offer.currency)
                    : "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Start Date
                </label>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {offer.startDate
                    ? formatDate(offer.startDate, { includeTime: false })
                    : "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Expiry Date
                </label>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {offer.expiryDate
                    ? formatDate(offer.expiryDate, { includeTime: false })
                    : "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Location
                </label>
                <p className="text-gray-900 capitalize break-words whitespace-pre-wrap">
                  {offer.location || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Probation Period
                </label>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {offer.probationPeriod
                    ? `${offer.probationPeriod} months`
                    : "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Notice Period
                </label>
                <p className="text-gray-900 break-words whitespace-pre-wrap">
                  {offer.noticePeriod ? `${offer.noticePeriod} days` : "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Benefits
            </h2>
            <div className="flex flex-wrap gap-2">
              {offer.benefits && offer.benefits.length > 0 ? (
                offer.benefits.map((benefit, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    {benefit}
                  </span>
                ))
              ) : (
                <p className="text-gray-500 italic">No benefits specified</p>
              )}
            </div>
          </div>

          {/* Terms & Conditions */}
          {offer.terms &&
            (offer.terms.workingHours ||
              offer.terms.leavePolicy ||
              offer.terms.otherTerms) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
                <div className="space-y-4">
                  {offer.terms.workingHours && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Working Hours
                      </label>
                      <p className="text-gray-900">
                        {offer.terms.workingHours}
                      </p>
                    </div>
                  )}
                  {offer.terms.leavePolicy && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Leave Policy
                      </label>
                      <p className="text-gray-900">{offer.terms.leavePolicy}</p>
                    </div>
                  )}
                  {offer.terms.otherTerms && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Other Terms
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-lg border">
                        {offer.terms.otherTerms
                          .split("\n")
                          .map((line, index) => {
                            const trimmedLine = line.trim();

                            // Check if line starts with a number followed by a dot (like "1.", "2.", etc.)
                            if (/^\d+\./.test(trimmedLine)) {
                              return (
                                <div key={index} className="mb-3">
                                  <div className="font-bold text-gray-900 text-base mb-1 bg-blue-50 px-3 py-2 rounded-md border-l-4 border-blue-400 shadow-sm">
                                    {trimmedLine}
                                  </div>
                                </div>
                              );
                            }

                            // Check if line contains sub-points or bullet points
                            if (
                              trimmedLine.startsWith("-") ||
                              trimmedLine.startsWith("•") ||
                              trimmedLine.startsWith("*")
                            ) {
                              return (
                                <div key={index} className="mb-1 pl-6">
                                  <span className="text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                    {line}
                                  </span>
                                </div>
                              );
                            }

                            // Check if line contains colons (likely sub-headings)
                            if (
                              trimmedLine.includes(":") &&
                              trimmedLine.length < 50
                            ) {
                              return (
                                <div key={index} className="mb-2 pl-4">
                                  <span className="font-semibold text-gray-800 bg-green-50 px-2 py-1 rounded border-l-2 border-green-400">
                                    {line}
                                  </span>
                                </div>
                              );
                            }

                            // Regular lines
                            if (trimmedLine.length > 0) {
                              return (
                                <div key={index} className="mb-1 pl-2">
                                  <span className="text-gray-700">{line}</span>
                                </div>
                              );
                            }

                            return null;
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Notes */}
          {offer.notes && offer.notes !== "No notes" && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Notes
              </h2>
              <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded-lg border max-h-64 overflow-y-auto shadow-sm whitespace-pre-wrap break-words">
                {offer.notes.split("\n").map((line, index) => {
                  const trimmedLine = line.trim();

                  // Check if line starts with a number followed by a dot (like "1.", "2.", etc.)
                  if (/^\d+\./.test(trimmedLine)) {
                    return (
                      <div key={index} className="mb-3">
                        <div className="font-bold text-gray-900 text-base mb-1 bg-blue-50 px-3 py-2 rounded-md border-l-4 border-blue-400 shadow-sm">
                          {trimmedLine}
                        </div>
                      </div>
                    );
                  }

                  // Check if line contains rating prompts or checkboxes
                  if (
                    trimmedLine.includes("Rating (1-5)") ||
                    trimmedLine.includes("☐") ||
                    trimmedLine.includes("➡️")
                  ) {
                    return (
                      <div key={index} className="mb-2 pl-6">
                        <span className="text-gray-700 bg-yellow-50 px-2 py-1 rounded border-l-2 border-yellow-300">
                          {line}
                        </span>
                      </div>
                    );
                  }

                  // Check if line contains sub-points or bullet points
                  if (
                    trimmedLine.startsWith("-") ||
                    trimmedLine.startsWith("•") ||
                    trimmedLine.startsWith("*")
                  ) {
                    return (
                      <div key={index} className="mb-1 pl-6">
                        <span className="text-gray-700 bg-gray-100 px-2 py-1 rounded">
                          {line}
                        </span>
                      </div>
                    );
                  }

                  // Check if line contains colons (likely sub-headings)
                  if (trimmedLine.includes(":") && trimmedLine.length < 50) {
                    return (
                      <div key={index} className="mb-2 pl-4">
                        <span className="font-semibold text-gray-800 bg-green-50 px-2 py-1 rounded border-l-2 border-green-400">
                          {line}
                        </span>
                      </div>
                    );
                  }

                  // Check for specific keywords that should be highlighted
                  if (
                    trimmedLine.includes("Strong") ||
                    trimmedLine.includes("Weak") ||
                    trimmedLine.includes("Good") ||
                    trimmedLine.includes("Excellent") ||
                    trimmedLine.includes("Average")
                  ) {
                    return (
                      <div key={index} className="mb-2">
                        <span className="font-medium text-gray-800 bg-green-50 px-3 py-1 rounded border-l-2 border-green-400">
                          {trimmedLine}
                        </span>
                      </div>
                    );
                  }

                  // Regular lines
                  if (trimmedLine.length > 0) {
                    return (
                      <div key={index} className="mb-1 pl-2">
                        <span className="text-gray-700">{line}</span>
                      </div>
                    );
                  }

                  // Empty lines
                  return <div key={index} className="mb-2"></div>;
                })}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Offer Letter
            </h2>
            <div className="space-y-3">
              {offer.documents && offer.documents.length > 0 ? (
                offer.documents.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          {doc.originalName ||
                            doc.filename ||
                            `Document ${index + 1}`}
                        </span>
                        {doc.fileSize && (
                          <p className="text-xs text-gray-500">
                            {(doc.fileSize / 1024).toFixed(1)} KB
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDownloadDocument(doc)}
                        className="text-[#0F2942] hover:text-blue-800"
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No documents attached</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Status Actions
            </h3>
            <div className="space-y-3">
              {offer.status === "draft" && (
                <Button
                  variant="primary"
                  className={`w-full ${greenButtonClasses}`}
                  onClick={handleSendOffer}
                  disabled={updating}
                >
                  {updating ? "Sending..." : "Send Offer"}
                </Button>
              )}
              {offer.status === "sent" && (
                <>
                  <Button
                    variant="primary"
                    className={`w-full ${greenButtonClasses}`}
                    onClick={() => handleStatusChange("accepted")}
                    disabled={updating}
                  >
                    Mark Accepted
                  </Button>
                  <Button
                    variant="secondary"
                    className={`w-full ${greenButtonClasses}`}
                    onClick={() => handleStatusChange("rejected")}
                    disabled={updating}
                  >
                    Mark Rejected
                  </Button>
                </>
              )}
              {offer.status === "pending" && (
                <>
                  <Button
                    variant="primary"
                    className={`w-full ${greenButtonClasses}`}
                    onClick={() => handleStatusChange("accepted")}
                    disabled={updating}
                  >
                    Mark Accepted
                  </Button>
                  <Button
                    variant="secondary"
                    className={`w-full ${greenButtonClasses}`}
                    onClick={() => handleStatusChange("rejected")}
                    disabled={updating}
                  >
                    Mark Rejected
                  </Button>
                </>
              )}
              {offer.status !== "sent" && offer.status !== "accepted" && (
                <PermissionGate permission="recruitment" action="update">
                  <Button
                    variant="secondary"
                    className={`w-full ${greenButtonClasses}`}
                    onClick={() => navigate(`/edit_offer/${id}`)}
                    disabled={updating}
                  >
                    Edit Offer
                  </Button>
                </PermissionGate>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 break-words">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Timeline
            </h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Offer Created
                  </p>
                  <p className="text-xs text-gray-500">
                    {offer.createdAt
                      ? formatDate(offer.createdAt, { includeTime: false })
                      : "N/A"}
                  </p>
                </div>
              </div>
              {offer.sentDate && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Offer Sent
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(offer.sentDate, { includeTime: false })}
                    </p>
                  </div>
                </div>
              )}
              {offer.acceptedDate && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Offer Accepted
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(offer.acceptedDate, { includeTime: false })}
                    </p>
                  </div>
                </div>
              )}
              {offer.rejectedDate && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Offer Rejected
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(offer.rejectedDate, { includeTime: false })}
                    </p>
                  </div>
                </div>
              )}
              {offer.expiryDate && (
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Expire Date
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(offer.expiryDate, { includeTime: false })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Offer Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Offer Information
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Offer ID
                </label>
                <p className="text-sm text-gray-900">
                  {offer.offerId || `#${offer.id}`}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Last Updated
                </label>
                <p className="text-sm text-gray-900">
                  {offer.updatedAt
                    ? formatDate(offer.updatedAt, { includeTime: false })
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Rejection Reason */}
          {offer.status === "rejected" && offer.rejectionReason && (
            <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-6">
              <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Rejection Reason
              </h3>
              <p className="text-sm text-red-900 leading-relaxed whitespace-pre-wrap break-words">
                {offer.rejectionReason.charAt(0).toUpperCase() +
                  offer.rejectionReason.slice(1)}
              </p>
              {offer.rejectedDate && (
                <p className="text-xs text-red-400 mt-3">
                  Declined on{" "}
                  {formatDate(offer.rejectedDate, { includeTime: false })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom Alert and Confirm Components */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />

      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        view
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default ViewOffer;
